"use client";

import React, { useEffect, useState, useRef } from "react";
import { usePathname } from "next/navigation";
import { Locale } from "@/i18n/routing";
import { Banner } from "@/types/contentful/banner";
import PromotionalBannerClient from "./promotional-banner-client";
import PromotionalBannerAnalytics from "./promotional-banner-analytics";

interface PromotionalBannerClientWrapperProps {
  locale: Locale;
  initialBanner: Banner | null;
  initialPathname: string;
  previewMode: boolean;
}

/**
 * Client component that manages banner updates during navigation
 * Uses initial server-rendered banner, then fetches on route changes
 */
export default function PromotionalBannerClientWrapper({
  locale,
  initialBanner,
  initialPathname,
  previewMode
}: PromotionalBannerClientWrapperProps) {
  const pathname = usePathname();
  const [banner, setBanner] = useState<Banner | null>(initialBanner);
  const [isLoading, setIsLoading] = useState(false);
  const previousPathnameRef = useRef<string>(initialPathname);



  useEffect(() => {
    // Only fetch if pathname has actually changed
    if (pathname === previousPathnameRef.current) {
      return;
    }

    // Update the previous pathname
    previousPathnameRef.current = pathname;

    const fetchBanner = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `/api/promotional-banner?locale=${locale}&pathname=${encodeURIComponent(pathname)}&usePreview=${previewMode}`
        );

        if (response.ok) {
          const data = await response.json();
          setBanner(data);
        } else {
          setBanner(null);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching banner:", error);
        setBanner(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBanner();
  }, [pathname, locale, previewMode]);

  // Don't render anything while loading or if no banner
  if (isLoading || !banner) {
    return null;
  }

  return (
    <>
      {/* Set banner height in CSS to prevent layout shift */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            :root {
              --banner-height: 38px;
            }
            @media (max-width: 991px) {
              :root {
                --banner-height: 0px;
              }
            }
          `
        }}
      />
      <PromotionalBannerClient banner={banner} />
      <PromotionalBannerAnalytics banner={banner} />
    </>
  );
}
